// Account-wide FK sweep -- the single-click surface for fk_sweep (see
// /Users/steffanic/.claude/plans/spicy-wobbling-nest.md). Value-evidence
// only: no column-name matching anywhere in the underlying engine.
import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { runAccountFkSweep, getActiveClients, getFkGraph, dryRun, apply as applyChangeset } from './api';
import FkGraph from './FkGraph';
import './AccountFkSweep.css';

const uuid = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function SummaryChips({ result }) {
    return (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${result.applied.length} applied`}
                sx={{ bgcolor: 'var(--fk-status-good)', color: '#fff' }} />
            <Chip size="small" label={`${result.queued_for_review.length} needs review`}
                sx={{ bgcolor: 'var(--fk-status-warning)', color: '#000' }} />
            {result.needs_hub_pick.length > 0 && (
                <Chip size="small" label={`${result.needs_hub_pick.length} hub tie(s)`}
                    sx={{ bgcolor: 'var(--fk-status-critical)', color: '#fff' }} />
            )}
            {result.profile_errors.length > 0 && (
                <Chip size="small" label={`${result.profile_errors.length} unreadable file(s)`} variant="outlined" />
            )}
            <Chip size="small" variant="outlined" label={`status: ${result.changeset_status}`} />
        </Box>
    );
}

function HubTieCallout({ ties, onPick }) {
    return (
        <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {ties.length} hub tie{ties.length === 1 ? '' : 's'} need a pick before those columns can be linked.
            </Typography>
            {ties.map((tie, i) => (
                <Box key={i} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        Partition {tie.partition_title}:
                    </Typography>
                    {tie.candidates.map((c) => (
                        <Button key={c.datapoint_id} size="small" variant="outlined" sx={{ mr: 1, mt: 0.5 }}
                            onClick={() => onPick(c.datapoint_id)}>
                            Use {c.segment_name} ({c.non_null_count} rows, {Math.round(c.uniqueness_ratio * 100)}% unique)
                        </Button>
                    ))}
                </Box>
            ))}
        </Alert>
    );
}

function ReviewTable({ rows, onAccepted }) {
    const [busyId, setBusyId] = useState(null);
    const [dismissed, setDismissed] = useState(() => new Set());
    const [errorFor, setErrorFor] = useState({});

    const accept = async (row) => {
        setBusyId(row.local_datapoint_id);
        setErrorFor((e) => ({ ...e, [row.local_datapoint_id]: null }));
        try {
            const changeset = {
                changeset_id: uuid(), origin: 'operator',
                ops: [{
                    op_id: uuid(), type: 'relink_fk',
                    target: { datapoint_id: row.local_datapoint_id },
                    params: { foreign_key_id: row.hub_datapoint_id },
                }],
            };
            const preview = await dryRun(changeset);
            if (preview.ops.some((o) => o.errors?.length)) {
                throw new Error(preview.ops.flatMap((o) => o.errors).map((e) => e.message).join('; '));
            }
            await applyChangeset(changeset);
            setDismissed((d) => new Set(d).add(row.local_datapoint_id));
            onAccepted?.(row);
        } catch (e) {
            setErrorFor((prev) => ({ ...prev, [row.local_datapoint_id]: e.message }));
        } finally {
            setBusyId(null);
        }
    };

    const visible = rows.filter((r) => !dismissed.has(r.local_datapoint_id));
    if (visible.length === 0) return null;

    return (
        <Box sx={{ mt: 2 }}>
            <Typography className="d-font" sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                Needs review ({visible.length})
            </Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Provider / segment</TableCell>
                        <TableCell>Column</TableCell>
                        <TableCell>Suggested hub</TableCell>
                        <TableCell align="right">Score</TableCell>
                        <TableCell align="right">Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visible.map((row) => (
                        <TableRow key={row.local_datapoint_id} hover>
                            <TableCell sx={{ fontSize: 12 }}>
                                {row.local_source_title} / {row.local_segment_name}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{row.local_actual_name}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                                {row.hub_source_title} / {row.hub_segment_name}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 12 }}>{row.score}</TableCell>
                            <TableCell align="right">
                                <Button size="small" variant="outlined"
                                    disabled={busyId === row.local_datapoint_id}
                                    onClick={() => accept(row)}>
                                    {busyId === row.local_datapoint_id ? <CircularProgress size={14} /> : 'Accept'}
                                </Button>
                                <Button size="small" sx={{ ml: 0.5 }}
                                    onClick={() => setDismissed((d) => new Set(d).add(row.local_datapoint_id))}>
                                    Dismiss
                                </Button>
                                {errorFor[row.local_datapoint_id] && (
                                    <Typography sx={{ fontSize: 11, color: 'var(--fk-status-critical)' }}>
                                        {errorFor[row.local_datapoint_id]}
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}

function ProgressLog({ lines }) {
    const boxRef = useRef(null);
    useEffect(() => {
        if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }, [lines]);
    if (lines.length === 0) return null;
    return (
        <Box ref={boxRef} className="fk-sweep-progress-log" sx={{ mb: 2 }}>
            {lines.map((line, i) => (
                <div key={i} className="fk-sweep-progress-line">
                    {i === lines.length - 1 ? <CircularProgress size={10} sx={{ mr: 1 }} /> : null}
                    {line}
                </div>
            ))}
        </Box>
    );
}

function CurrentGraphExplorer({ client }) {
    const [graph, setGraph] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!client) { setGraph(null); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getFkGraph(client.client_id)
            .then((r) => { if (!cancelled) setGraph(r); })
            .catch((e) => { if (!cancelled) setError(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [client]);

    if (!client) return null;

    return (
        <Box className="panel-inset" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography className="d-font" sx={{ fontSize: 13, fontWeight: 600 }}>
                    Currently linked ({client.title})
                </Typography>
                {loading && <CircularProgress size={14} />}
            </Box>
            <Typography className="d-font" sx={{ fontSize: 11, color: 'var(--color-text-muted)', mb: 1 }}>
                What's already set today -- pure metadata, no blob reads, so this loads instantly and
                works while a sweep is running (on this client or any other).
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {graph && (
                <FkGraph current={graph.edges} emptyMessage="No FK links set for this client yet." />
            )}
        </Box>
    );
}

export default function AccountFkSweep() {
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [clientsError, setClientsError] = useState(null);
    const [client, setClient] = useState(null); // {client_id, title} | null
    const [loading, setLoading] = useState(null); // 'dry-run' | 'apply' | null
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [hubOverrides, setHubOverrides] = useState([]);
    const [progressLog, setProgressLog] = useState([]);

    useEffect(() => {
        let cancelled = false;
        getActiveClients()
            .then((r) => { if (!cancelled) setClients(r.clients); })
            .catch((e) => { if (!cancelled) setClientsError(e.message); })
            .finally(() => { if (!cancelled) setClientsLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const run = async (doApply) => {
        if (!client) return;
        setLoading(doApply ? 'apply' : 'dry-run');
        setError(null);
        setProgressLog([]);
        try {
            const r = await runAccountFkSweep(client.client_id, {
                apply: doApply, hubOverrides,
                onProgress: (message) => setProgressLog((prev) => [...prev, message]),
            });
            setResult(r);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(null);
        }
    };

    const pickHub = (datapointId) => {
        setHubOverrides((prev) => [...prev, datapointId]);
    };

    return (
        <Box>
            <Typography className="d-font" sx={{ fontSize: 12, color: 'var(--color-text-secondary)', mb: 2 }}>
                Profiles every column of every registered segment for a client by real value
                overlap (never by column name), and relinks anything high-confidence through the
                same audited relink_fk changeset used everywhere else in this panel. Can take
                several minutes on a large account -- this is a plain long-running request, not a
                background job.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 340 }}
                    options={clients}
                    loading={clientsLoading}
                    getOptionLabel={(c) => c.title || ''}
                    isOptionEqualToValue={(a, b) => a.client_id === b.client_id}
                    value={client}
                    onChange={(_, v) => { setClient(v); setResult(null); setHubOverrides([]); }}
                    renderInput={(params) => (
                        <TextField {...params} label="Client"
                            helperText={clientsError ? `Couldn't load clients: ${clientsError}` : ' '}
                            error={!!clientsError}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {clientsLoading ? <CircularProgress size={14} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }} />
                    )}
                />
                <Button variant="outlined" disabled={!client || !!loading} onClick={() => run(false)}>
                    {loading === 'dry-run' ? <CircularProgress size={16} /> : 'Run sweep (dry-run)'}
                </Button>
                <Button variant="contained" disabled={!client || !!loading || !result}
                    onClick={() => run(true)}>
                    {loading === 'apply' ? <CircularProgress size={16} /> : 'Apply auto-eligible links'}
                </Button>
                {hubOverrides.length > 0 && (
                    <Chip size="small" variant="outlined"
                        label={`${hubOverrides.length} hub override(s) staged -- re-run to use them`} />
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <ProgressLog lines={progressLog} />

            {result && (
                <>
                    <SummaryChips result={result} />
                    {result.needs_hub_pick.length > 0 && (
                        <HubTieCallout ties={result.needs_hub_pick} onPick={pickHub} />
                    )}
                    <FkGraph applied={result.applied} queued={result.queued_for_review} />
                    <ReviewTable rows={result.queued_for_review} />
                </>
            )}

            <CurrentGraphExplorer client={client} />
        </Box>
    );
}
